import React from "react";
import { signedPercent } from "src/utils/number";
import styled from "styled-components";
import TrendMark from "src/components/TrendMark";
import { colors } from "src/theme/colors";
import { tone as tones, Tone } from "src/theme/ui";
import { toPerformanceNumber } from "src/utils/performances";

/**
 * A figure for one period.
 *
 * The product's one shape for this, because it appears in two places that
 * had invented it twice — the smartfolio list drew tinted pills with PNG
 * arrows and six hex literals, and the smartfolio page drew the same thing
 * again through an Ant Design tab bar.
 *
 * A figure, not a badge: several in a row read as things being FLAGGED when
 * they are readings of the same measure over different spans, which is a
 * small table. The period above says what it is and the tone says which way
 * it went, so a container adds nothing — and without one the figures line up
 * on a fixed column width and tabular digits, so they can be compared across
 * a row and down a list.
 */
/* Centred, not baseline-aligned, when the pair is on one line.
   Figure is an inline-flex, and an inline-flex box takes its baseline from
   its FIRST FLEX ITEM — which here is the trend arrow, not the digits. So
   sharing a baseline with it sat the small period label below the figure's
   own text baseline, by however much that glyph's box happened to differ,
   which is why one period in a row of them looked out of line with the
   rest. Centring the two ignores the arrow's box entirely. */
const Wrap = styled.div<{ $align: string; $inline: boolean }>`
  display: flex;
  flex-direction: ${({ $inline }) => ($inline ? "row" : "column")};
  align-items: ${({ $align, $inline }) => ($inline ? "center" : $align)};
  gap: ${({ $inline }) => ($inline ? "7px" : "3px")};
  min-width: ${({ $inline }) => ($inline ? 0 : "58px")};

  @media only screen and (max-width: 767px) {
    flex-direction: ${({ $inline }) => ($inline ? "row" : "row-reverse")};
    align-items: center;
    gap: 6px;
    min-width: 0;
  }
`;

const Period = styled.span`
  font-size: 9.5px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${colors.gray[500]};
  white-space: nowrap;
`;

const Figure = styled.span<{ $tone: Tone; $plain: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  color: ${({ $tone, $plain }) =>
    $plain ? colors.gray[900] : tones[$tone].fg};
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
  white-space: nowrap;

  @media only screen and (max-width: 767px) {
    font-size: 11px;
  }
`;

type Props = {
  /** The span this figure covers — "1M", "YTD". */
  period: string;
  /** The raw value, as a fraction. */
  value?: unknown;
  /**
   * A figure with no direction, such as a volatility.
   *
   * It takes ink and no arrow. Toning a volatility green because it happens
   * to be positive says a holding GAINED when what it did was move about,
   * and every volatility is positive, so the whole column would be green.
   */
  plain?: boolean;
  align?: "flex-start" | "flex-end" | "center";
  /**
   * Period and figure on one line, for a slim readout such as the tape.
   *
   * A column pair is right where several of them stand side by side and the
   * labels form a header row to read across; on a single running line there
   * is no row to form, and stacking only makes the strip twice as tall as
   * the text in it.
   */
  inline?: boolean;
};

const PeriodReturn: React.FC<Props> = ({
  period,
  value,
  plain = false,
  align = "flex-end",
  inline = false,
}) => {
  const n = toPerformanceNumber(value);
  /* An absent figure is not a flat one: it takes the neutral tone but a
     dash, rather than a 0.00% the holding never returned. */
  const tone: Tone = n === null || n === 0 ? "flat" : n > 0 ? "up" : "down";

  return (
    <Wrap $align={align} $inline={inline}>
      <Period>{period}</Period>
      <Figure $tone={tone} $plain={plain}>
        {n === null ? (
          "—"
        ) : (
          <>
            {!plain && <TrendMark $tone={tone} size={11} />}
            {signedPercent(n * 100)}
          </>
        )}
      </Figure>
    </Wrap>
  );
};

export default PeriodReturn;
